import { useCallback, useEffect, useState } from "react";
import { api, apiErrorMessage } from "@/api/client";

type EmailStatus = "success" | "failure";

interface EmailLogRow {
  id: number;
  status: EmailStatus;
  to: string;
  subject: string;
  error_message: string | null;
  sent_at: string | null;
}

interface ListResp {
  email_logs: EmailLogRow[];
  pagination: {
    page: number;
    per_page: number;
    total: number;
    total_pages: number;
  };
  filter: {
    status: EmailStatus | null;
    days: number;
  };
}

interface SummaryResp {
  window_hours: number;
  since: string;
  success: number;
  failure: number;
  total: number;
}

const PER_PAGE = 50;

function formatDateTime(iso: string | null): string {
  if (!iso) return "—";
  const d = new Date(iso);
  if (isNaN(d.getTime())) return iso;
  return d.toLocaleString("ja-JP", {
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
  });
}

export function AdminEmailLogsPage() {
  const [statusFilter, setStatusFilter] = useState<"" | EmailStatus>("");
  const [days, setDays] = useState(30);
  const [page, setPage] = useState(1);

  const [data, setData] = useState<ListResp | null>(null);
  const [summary, setSummary] = useState<SummaryResp | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [expanded, setExpanded] = useState<Record<number, boolean>>({});

  const load = useCallback(() => {
    setLoading(true);
    setError(null);
    const params: Record<string, string | number> = {
      page,
      per_page: PER_PAGE,
      days,
    };
    if (statusFilter) params.status = statusFilter;
    Promise.all([
      api.get<ListResp>("/admin/email-logs", { params }),
      api.get<SummaryResp>("/admin/email-logs/summary", {
        params: { hours: 24 },
      }),
    ])
      .then(([list, sum]) => {
        setData(list.data);
        setSummary(sum.data);
      })
      .catch((e) =>
        setError(apiErrorMessage(e, "メール送信ログの取得に失敗しました")),
      )
      .finally(() => setLoading(false));
  }, [page, statusFilter, days]);

  useEffect(() => {
    load();
  }, [load]);

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-semibold">📧 メール送信ログ</h1>
        <p className="text-sm text-gray-600 mt-1">
          全送信メール（申込通知・退会通知・発注通知等）の成功/失敗履歴を確認できます。
          SendGrid のトラブルで気付かずメールが止まる事故を早期検知するための監視機構です。
        </p>
      </div>

      {error && (
        <div className="rounded-md bg-red-50 border border-red-200 px-4 py-3 text-sm text-red-800">
          {error}
        </div>
      )}

      {/* サマリーカード */}
      {summary && (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div
            className={
              "card p-4 " +
              (summary.failure > 0
                ? "border-red-300 bg-red-50"
                : "border-green-200 bg-green-50")
            }
          >
            <div className="text-xs text-gray-600">直近 {summary.window_hours} 時間</div>
            <div className="text-2xl font-bold mt-1">
              {summary.failure > 0 ? "⚠️ " : "✅ "}
              失敗 {summary.failure} 件 / 成功 {summary.success} 件
            </div>
            <div className="text-xs text-gray-500 mt-1">
              失敗が 0 でない場合は SendGrid 等の問題の可能性があります
            </div>
          </div>
        </div>
      )}

      {/* フィルタ */}
      <div className="card p-4 flex flex-wrap items-end gap-4">
        <div>
          <label className="label text-xs">ステータス</label>
          <select
            className="input"
            value={statusFilter}
            onChange={(e) => {
              setStatusFilter(e.target.value as "" | EmailStatus);
              setPage(1);
            }}
          >
            <option value="">すべて</option>
            <option value="failure">失敗のみ</option>
            <option value="success">成功のみ</option>
          </select>
        </div>
        <div>
          <label className="label text-xs">直近</label>
          <select
            className="input"
            value={days}
            onChange={(e) => {
              setDays(Number(e.target.value));
              setPage(1);
            }}
          >
            <option value={1}>1 日</option>
            <option value={7}>7 日</option>
            <option value={30}>30 日</option>
            <option value={90}>90 日</option>
          </select>
        </div>
        <div className="ml-auto">
          <button
            type="button"
            className="btn-secondary"
            onClick={load}
            disabled={loading}
          >
            {loading ? "読み込み中…" : "🔄 再読み込み"}
          </button>
        </div>
      </div>

      {/* 一覧 */}
      <div className="card overflow-hidden">
        <table className="min-w-full text-sm">
          <thead className="bg-gray-100">
            <tr>
              <th className="px-3 py-2 text-left">送信日時</th>
              <th className="px-3 py-2 text-left">状態</th>
              <th className="px-3 py-2 text-left">宛先</th>
              <th className="px-3 py-2 text-left">件名</th>
              <th className="px-3 py-2 text-left">エラー</th>
            </tr>
          </thead>
          <tbody>
            {data?.email_logs.length === 0 && !loading && (
              <tr>
                <td colSpan={5} className="text-center text-gray-500 py-6">
                  該当する送信ログはありません
                </td>
              </tr>
            )}
            {data?.email_logs.map((r) => (
              <tr
                key={r.id}
                className={
                  "border-t " +
                  (r.status === "failure" ? "bg-red-50" : "bg-white")
                }
              >
                <td className="px-3 py-2 font-mono text-xs whitespace-nowrap">
                  {formatDateTime(r.sent_at)}
                </td>
                <td className="px-3 py-2 whitespace-nowrap">
                  {r.status === "failure" ? (
                    <span className="badge badge-red">失敗</span>
                  ) : (
                    <span className="badge badge-green">成功</span>
                  )}
                </td>
                <td className="px-3 py-2 text-xs">{r.to}</td>
                <td className="px-3 py-2">{r.subject}</td>
                <td className="px-3 py-2 text-xs text-gray-700 max-w-md">
                  {r.error_message ? (
                    <div>
                      <button
                        type="button"
                        className="text-blue-600 hover:underline text-xs"
                        onClick={() =>
                          setExpanded((s) => ({ ...s, [r.id]: !s[r.id] }))
                        }
                      >
                        {expanded[r.id] ? "▼ 折りたたむ" : "▶ 詳細を表示"}
                      </button>
                      {expanded[r.id] && (
                        <pre className="mt-1 whitespace-pre-wrap break-all bg-gray-100 p-2 rounded text-xs text-gray-800">
                          {r.error_message}
                        </pre>
                      )}
                    </div>
                  ) : (
                    "—"
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* ページング */}
      {data && data.pagination.total_pages > 1 && (
        <div className="flex justify-between items-center text-sm">
          <div className="text-gray-600">
            全 {data.pagination.total} 件 / {data.pagination.page} / {data.pagination.total_pages} ページ
          </div>
          <div className="flex gap-2">
            <button
              type="button"
              className="btn-secondary"
              disabled={data.pagination.page <= 1}
              onClick={() => setPage((p) => Math.max(1, p - 1))}
            >
              ← 前へ
            </button>
            <button
              type="button"
              className="btn-secondary"
              disabled={data.pagination.page >= data.pagination.total_pages}
              onClick={() => setPage((p) => p + 1)}
            >
              次へ →
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
